/**
 * ─────────────────────────────────────────────────────────────────
 * Comparador de precios entre supermercados.
 *
 * VErsión móvil:
 *  - isMobile con useMediaQuery (reactivo)
 *  - Header compacto en móvil (h5 sin icono grande)
 *  - Cards de resultado: layout horizontal compacto en móvil
 *    imagen 56px | nombre+badge+botón | precio
 *  - Artículos de lista: lista vertical en móvil (antes grid horizontal)
 *  - Buscador: campo + selector apilados verticalmente ya estaba bien
 *  - Cesta: botones full-width en móvil, controles de cantidad táctiles
 *  - Todos los botones interactivos con minHeight 44px
 * ─────────────────────────────────────────────────────────────────
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Box, Typography, Card, CardContent, Stack,
    CircularProgress, TextField, Button, MenuItem,
    IconButton, Avatar, Divider, Paper,
    Checkbox, FormControlLabel, Fade, Alert,
    useTheme, useMediaQuery,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";

import CompareArrowsIcon  from "@mui/icons-material/CompareArrows";
import SearchIcon         from "@mui/icons-material/Search";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import AddIcon            from "@mui/icons-material/Add";
import RemoveIcon         from "@mui/icons-material/Remove";
import DeleteOutlineIcon  from "@mui/icons-material/DeleteOutline";
import SaveIcon           from "@mui/icons-material/Save";
import ArrowBackIcon      from "@mui/icons-material/ArrowBack";
import CheckCircleIcon    from "@mui/icons-material/CheckCircle";
import ListAltIcon        from "@mui/icons-material/ListAlt";
import StorefrontIcon     from "@mui/icons-material/Storefront";
import IosShareIcon      from "@mui/icons-material/IosShare";
import { useCompartir, formatearSeleccionParaCompartir } from "../hooks/useCompartir";

import { comparePrices }                        from "../api/prices";
import { agregarArticulosBatch, obtenerArticulos } from "../api/articulos";
import { obtenerLista }                          from "../api/listas";
import GuardarEnListaModal                       from "../components/GuardarEnListaModal";

export default function Compare() {
    const MotionCard = motion(Card);
    const MotionBox  = motion(Box);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const theme    = useTheme();
    const idLista  = searchParams.get("lista");

    const { compartir, compartiendo } = useCompartir();

    /** isMobile reactivo */
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [nombreLista,        setNombreLista]        = useState("");
    const [articulosLista,     setArticulosLista]      = useState([]);
    const [recordatorioChecked,setRecordatorioChecked] = useState({});

    const [query,    setQuery]    = useState("");
    const [provider, setProvider] = useState("all");
    const [items,    setItems]    = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [loading,  setLoading]  = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);
    const [okMsg,    setOkMsg]    = useState(null);
    const [mostrarGuardarModal, setMostrarGuardarModal] = useState(false);

    /** Carga los artículos de la lista si venimos desde una lista */
    useEffect(() => {
        if (!idLista) return;
        obtenerLista(idLista).then((l) => setNombreLista(l.nombre));
        obtenerArticulos(idLista)
            .then((arts) => {
                const lista = Array.isArray(arts) ? arts : [];
                setArticulosLista(lista);
                const init = {};
                lista.forEach((a) => { init[a.idarticulo] = a.comprado; });
                setRecordatorioChecked(init);
            })
            .catch(() => setArticulosLista([]));
    }, [idLista]);

    /** Llama a la API de comparación de precios */
    const handleBuscar = async () => {
        if (!query.trim()) return;
        setLoading(true);
        setErrorMsg(null);
        try {
            const params = { q: query, limit: 20, offset: 0, strategy: "optima" };
            if (provider !== "all") params.provider = provider;
            const { data } = await comparePrices(params);
            setItems(data.items || []);
        } catch (err) {
            console.error(err);
            setErrorMsg(err?.response?.data?.detail || "No se pudieron cargar los resultados.");
        } finally {
            setLoading(false);
        }
    };

    /** Ordena resultados por precio ascendente */
    const sortedItems = useMemo(() => [...items].sort((a, b) => a.precio - b.precio), [items]);

    // ── Gestión de cesta ────────────────────────────────────────────
    const isSelected  = (item) => selectedItems.some((i) => i.id_catalogo === item.id_catalogo);
    const getCantidad = (item) => selectedItems.find((i) => i.id_catalogo === item.id_catalogo)?.cantidad || 0;
    const addItem     = (item) => setSelectedItems((prev) => [...prev, { ...item, cantidad: 1 }]);
    const increment   = (item) => setSelectedItems((prev) => prev.map((i) => i.id_catalogo === item.id_catalogo ? { ...i, cantidad: i.cantidad + 1 } : i));
    const decrement   = (item) => setSelectedItems((prev) => prev.map((i) => i.id_catalogo === item.id_catalogo ? { ...i, cantidad: Math.max(1, i.cantidad - 1) } : i));
    const removeItem  = (item) => setSelectedItems((prev) => prev.filter((i) => i.id_catalogo !== item.id_catalogo));
    const clearSelection = () => setSelectedItems([]);
    const total = selectedItems.reduce((sum, i) => sum + i.precio * i.cantidad, 0);

    /** Guarda los items seleccionados en una lista */
    async function guardarSeleccion(idListaDestino) {
        try {
            const payload = selectedItems.map((it) => ({
                nombre:          it.nombre_catalogo,
                cantidad:        it.cantidad,
                unidad:          "ud",
                precio_unitario: it.precio,
                supermercado:    it.nombre_tienda,
                origen:          provider,
            }));
            await agregarArticulosBatch(idListaDestino, payload);
            setOkMsg("Productos guardados en la lista");
            setSelectedItems([]);
            setTimeout(() => setOkMsg(null), 2500);
        } catch (e) {
            console.error(e);
            alert("Error guardando los productos en la lista");
        } finally {
            setMostrarGuardarModal(false);
        }
    }

    /**
     * Badge de supermercado — color según tienda.
     * Extraído como función para reutilizar en card y cesta.
     */
    const renderBadge = (tienda) => {
        const isMercadona = tienda?.toLowerCase().includes("mercadona");
        return (
            <Box sx={{
                display:       "inline-flex",
                px:            1,
                py:            0.25,
                borderRadius:  1,
                fontSize:      "0.68rem",
                fontWeight:    800,
                bgcolor:       isMercadona ? "rgba(237,108,2,0.1)" : "rgba(46,125,50,0.1)",
                color:         isMercadona ? "#ed6c02" : "#2e7d32",
                textTransform: "uppercase",
                letterSpacing: 0.5,
            }}>
                {isMercadona ? "Mercadona" : "HiperDino"}
            </Box>
        );
    };

    return (
        <Box sx={{ p: { xs: 1.5, md: 4 }, maxWidth: 1000, mx: "auto" }}>

            {/* ── HEADER ──────────────────────────────────────────────── */}
            <MotionBox
                initial={{ opacity: 0, y: -16 }}
                animate={{ opacity: 1, y: 0 }}
                sx={{ mb: isMobile ? 2 : 4 }}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                    <Typography
                        variant={isMobile ? "h5" : "h4"}
                        sx={{ fontWeight: 900, color: "primary.main", display: "flex", alignItems: "center", gap: 1 }}
                    >
                        {!isMobile && <CompareArrowsIcon sx={{ fontSize: 36 }} />}
                        Comparador
                    </Typography>

                    {/* Botón volver a lista si venimos desde una lista */}
                    {idLista && (
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate(`/listas/${idLista}`)}
                            size={isMobile ? "small" : "medium"}
                            sx={{ fontWeight: 700, textTransform: "none" }}
                        >
                            {isMobile ? `"${nombreLista}"` : `Volver a la lista "${nombreLista}"`}
                        </Button>
                    )}
                </Stack>
            </MotionBox>

            {/* ── RECORDATORIO DE LISTA ────────────────────────────────── */}
            {idLista && articulosLista.length > 0 && (
                <Fade in timeout={400}>
                    <Card sx={{
                        mb:           isMobile ? 2 : 4,
                        borderRadius: 3,
                        border:       "1px solid",
                        borderColor:  "divider",
                        boxShadow:    "0 2px 12px rgba(0,0,0,0.04)",
                    }}>
                        <CardContent sx={{ p: isMobile ? "12px !important" : "20px !important" }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                                <ListAltIcon fontSize="small" color="primary" />
                                Artículos en {nombreLista}
                            </Typography>

                            {/*
                             * En móvil: lista vertical (antes grid 3 columnas que se solapaba).
                             * Cada item ocupa toda la fila — más legible y táctil.
                             */}
                            <Stack spacing={0.5}>
                                {articulosLista.map((a) => (
                                    <FormControlLabel
                                        key={a.idarticulo}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={!!recordatorioChecked[a.idarticulo]}
                                                onChange={() =>
                                                    setRecordatorioChecked((prev) => ({
                                                        ...prev,
                                                        [a.idarticulo]: !prev[a.idarticulo],
                                                    }))
                                                }
                                                sx={{ py: isMobile ? "6px" : "4px" }}
                                            />
                                        }
                                        label={
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    textDecoration: recordatorioChecked[a.idarticulo] ? "line-through" : "none",
                                                    opacity:        recordatorioChecked[a.idarticulo] ? 0.5 : 1,
                                                    fontWeight:     600,
                                                }}
                                            >
                                                {a.nombre} — {a.cantidad} {a.unidad}
                                            </Typography>
                                        }
                                        sx={{ mx: 0 }}
                                    />
                                ))}
                            </Stack>
                        </CardContent>
                    </Card>
                </Fade>
            )}

            {/* ── BUSCADOR ─────────────────────────────────────────────── */}
            <Paper
                component="form"
                onSubmit={(e) => { e.preventDefault(); handleBuscar(); }}
                sx={{
                    p:            isMobile ? 2 : 3,
                    mb:           isMobile ? 2 : 4,
                    borderRadius: 3,
                    display:      "flex",
                    flexDirection: "column",
                    gap:          1.5,
                    boxShadow:    "0 4px 20px rgba(0,0,0,0.06)",
                    border:       "1px solid",
                    borderColor:  "divider",
                }}
            >
                {/* Campo de búsqueda */}
                <TextField
                    fullWidth
                    size={isMobile ? "small" : "medium"}
                    placeholder="Buscar producto (leche, arroz, agua...)"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    InputProps={{
                        startAdornment: <SearchIcon color="action" sx={{ mr: 1, fontSize: 20 }} />,
                        sx: { borderRadius: 2 },
                    }}
                />

                {/* Selector supermercado + botón buscar en la misma fila */}
                <Stack direction="row" spacing={1}>
                    <TextField
                        select
                        size={isMobile ? "small" : "medium"}
                        value={provider}
                        onChange={(e) => setProvider(e.target.value)}
                        sx={{ flex: 1 }}
                        InputProps={{ sx: { borderRadius: 2 } }}
                    >
                        <MenuItem value="all">Todos</MenuItem>
                        <MenuItem value="mercadona_algolia">Mercadona</MenuItem>
                        <MenuItem value="hiperdino_scraper">HiperDino</MenuItem>
                    </TextField>

                    <Button
                        variant="contained"
                        disabled={loading}
                        type="submit"
                        sx={{
                            borderRadius:  2,
                            px:            3,
                            fontWeight:    700,
                            minHeight:     isMobile ? 40 : 52,
                            textTransform: "none",
                            flexShrink:    0,
                        }}
                    >
                        {loading ? <CircularProgress size={20} color="inherit" /> : "Buscar"}
                    </Button>
                </Stack>
            </Paper>

            {/* ── MENSAJES OK / ERROR ──────────────────────────────────── */}
            <AnimatePresence>
                {okMsg && (
                    <MotionBox initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} sx={{ mb: 2 }}>
                        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ borderRadius: 3, fontWeight: 700 }}>{okMsg}</Alert>
                    </MotionBox>
                )}
                {errorMsg && (
                    <MotionBox initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} sx={{ mb: 2 }}>
                        <Alert severity="error" sx={{ borderRadius: 3, fontWeight: 700 }}>{errorMsg}</Alert>
                    </MotionBox>
                )}
            </AnimatePresence>

            {/* ── CESTA DE SELECCIONADOS ───────────────────────────────── */}
            <AnimatePresence>
                {selectedItems.length > 0 && (
                    <MotionCard
                        initial={{ opacity: 0, scale: 0.97 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.97 }}
                        sx={{
                            mb:           isMobile ? 2 : 4,
                            borderRadius: 3,
                            border:       "2px solid",
                            borderColor:  "primary.main",
                            overflow:     "hidden",
                        }}
                    >
                        {/* Header cesta — en móvil: título arriba, botones abajo en fila completa */}
                        <Box sx={{ px: 2, py: 1.5, bgcolor: "primary.main", color: "white" }}>
                            {isMobile ? (
                                /* MÓVIL: dos filas para evitar botones aplastados */
                                <Stack spacing={1}>
                                    {/* Fila 1: título */}
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <ShoppingBasketIcon fontSize="small" />
                                        Mi selección ({selectedItems.length})
                                    </Typography>
                                    {/* Fila 2: botones a ancho completo */}
                                    <Stack direction="row" spacing={1}>
                                        <Button
                                            fullWidth
                                            size="small"
                                            variant="outlined"
                                            color="inherit"
                                            onClick={clearSelection}
                                            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, minHeight: 40 }}
                                        >
                                            Limpiar
                                        </Button>
                                        <Button
                                            fullWidth
                                            size="small"
                                            variant="contained"
                                            onClick={() => setMostrarGuardarModal(true)}
                                            startIcon={<SaveIcon fontSize="small" />}
                                            sx={{
                                                textTransform: "none",
                                                fontWeight:    800,
                                                borderRadius:  2,
                                                bgcolor:       "white",
                                                color:         "primary.main",
                                                minHeight:     40,
                                                "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
                                            }}
                                        >
                                            Guardar
                                        </Button>
                                        {/* Botón compartir como icono — no necesita fullWidth */}
                                        <IconButton
                                            disabled={compartiendo}
                                            onClick={() => compartir({
                                                titulo: "Mi selección ComPrara",
                                                texto:  formatearSeleccionParaCompartir(selectedItems, total),
                                            })}
                                            sx={{ color: "white", minWidth: 44, minHeight: 40, flexShrink: 0 }}
                                            aria-label="Compartir selección"
                                        >
                                            <IosShareIcon />
                                        </IconButton>
                                    </Stack>
                                </Stack>
                            ) : (
                                /* ESCRITORIO: fila horizontal original */
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <ShoppingBasketIcon fontSize="small" />
                                        Mi selección ({selectedItems.length})
                                    </Typography>
                                    <Stack direction="row" spacing={1}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="inherit"
                                            onClick={clearSelection}
                                            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, minHeight: 32 }}
                                        >
                                            Limpiar
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={() => setMostrarGuardarModal(true)}
                                            startIcon={<SaveIcon fontSize="small" />}
                                            sx={{
                                                textTransform: "none",
                                                fontWeight:    800,
                                                borderRadius:  2,
                                                bgcolor:       "white",
                                                color:         "primary.main",
                                                minHeight:     32,
                                                "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
                                            }}
                                        >
                                            Guardar
                                        </Button>
                                    </Stack>
                                </Stack>
                            )}
                        </Box>

                        {/* Items de la cesta */}
                        <CardContent sx={{ p: "0 !important" }}>
                            <Stack divider={<Divider />}>
                                {selectedItems.map((it) => (
                                    <Box key={it.id_catalogo} sx={{ px: 1.5, py: 1 }}>
                                        {isMobile ? (
                                            /*
                                             * MÓVIL: 2 filas
                                             * Fila 1: imagen + nombre + badge + botón eliminar
                                             * Fila 2: controles cantidad + precio
                                             */
                                            <Stack spacing={0.75}>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Avatar src={it.image_url} variant="rounded" sx={{ width: 32, height: 32, bgcolor: "white", border: "1px solid", borderColor: "divider", flexShrink: 0 }} />
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                            {it.nombre_catalogo}
                                                        </Typography>
                                                        {renderBadge(it.nombre_tienda)}
                                                    </Box>
                                                    <IconButton size="small" color="error" onClick={() => removeItem(it)} sx={{ minWidth: 36, minHeight: 36, flexShrink: 0 }}>
                                                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Stack>
                                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                        <IconButton size="small" onClick={() => decrement(it)} sx={{ border: "1px solid", borderColor: "divider", minWidth: 32, minHeight: 32 }}>
                                                            <RemoveIcon sx={{ fontSize: 14 }} />
                                                        </IconButton>
                                                        <Typography sx={{ fontWeight: 800, minWidth: 20, textAlign: "center", fontSize: "0.85rem" }}>
                                                            {it.cantidad}
                                                        </Typography>
                                                        <IconButton size="small" onClick={() => increment(it)} sx={{ border: "1px solid", borderColor: "divider", minWidth: 32, minHeight: 32 }}>
                                                            <AddIcon sx={{ fontSize: 14 }} />
                                                        </IconButton>
                                                    </Stack>
                                                    <Typography variant="body2" sx={{ fontWeight: 900, color: "primary.main" }}>
                                                        {(it.precio * it.cantidad).toFixed(2)} €
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                        ) : (
                                            /* ESCRITORIO: fila horizontal original */
                                            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                                                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                                                    <Avatar src={it.image_url} variant="rounded" sx={{ width: 36, height: 36, bgcolor: "white", border: "1px solid", borderColor: "divider", flexShrink: 0 }} />
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                            {it.nombre_catalogo}
                                                        </Typography>
                                                        {renderBadge(it.nombre_tienda)}
                                                    </Box>
                                                </Stack>
                                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                                                    <IconButton size="small" onClick={() => decrement(it)} sx={{ border: "1px solid", borderColor: "divider", minWidth: 36, minHeight: 36 }}>
                                                        <RemoveIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                    <Typography sx={{ fontWeight: 800, minWidth: 20, textAlign: "center", fontSize: "0.9rem" }}>{it.cantidad}</Typography>
                                                    <IconButton size="small" onClick={() => increment(it)} sx={{ border: "1px solid", borderColor: "divider", minWidth: 36, minHeight: 36 }}>
                                                        <AddIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                    <Typography variant="body2" sx={{ fontWeight: 900, ml: 0.5, minWidth: 56, textAlign: "right" }}>
                                                        {(it.precio * it.cantidad).toFixed(2)} €
                                                    </Typography>
                                                    <IconButton size="small" color="error" onClick={() => removeItem(it)} sx={{ minWidth: 36, minHeight: 36 }}>
                                                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </Stack>
                                            </Stack>
                                        )}
                                    </Box>
                                ))}
                            </Stack>

                            {/* Total */}
                            <Box sx={{ px: 2, py: 1.5, bgcolor: "rgba(0,0,0,0.02)", textAlign: "right" }}>
                                <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 900 }}>
                                    Total: <Box component="span" sx={{ color: "primary.main" }}>{total.toFixed(2)} €</Box>
                                </Typography>
                            </Box>
                        </CardContent>
                    </MotionCard>
                )}
            </AnimatePresence>

            {/* ── RESULTADOS ───────────────────────────────────────────── */}
            <Stack spacing={isMobile ? 1.5 : 2}>
                <AnimatePresence>
                    {sortedItems.map((it, idx) => (
                        <MotionCard
                            key={it.id_catalogo}
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.04 }}
                            whileHover={!isMobile ? { y: -3, boxShadow: "0 8px 32px rgba(0,0,0,0.1)" } : undefined}
                            sx={{
                                borderRadius: 3,
                                border:       "1px solid",
                                borderColor:  "divider",
                                boxShadow:    "0 2px 8px rgba(0,0,0,0.04)",
                                overflow:     "hidden",
                            }}
                        >
                            <CardContent sx={{ p: isMobile ? "10px 12px !important" : "16px !important" }}>
                                {/*
                                 * Layout móvil: fila horizontal compacta
                                 *   [imagen 56px] [nombre + badge] [precio + botón]
                                 *
                                 * Layout escritorio: grid con imagen grande 80px
                                 */}
                                <Stack direction="row" spacing={isMobile ? 1.5 : 2} alignItems="center">
                                    {/* Imagen producto */}
                                    <Avatar
                                        src={it.image_url}
                                        variant="rounded"
                                        sx={{
                                            width:      isMobile ? 56 : 80,
                                            height:     isMobile ? 56 : 80,
                                            bgcolor:    "white",
                                            border:     "1px solid",
                                            borderColor: "divider",
                                            flexShrink: 0,
                                            "& img":    { objectFit: "contain", p: "4px" },
                                        }}
                                    />

                                    {/* Nombre + badge — ocupa el espacio disponible */}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            variant={isMobile ? "body2" : "subtitle1"}
                                            sx={{
                                                fontWeight:   800,
                                                mb:           0.5,
                                                overflow:     "hidden",
                                                textOverflow: "ellipsis",
                                                // En móvil permitimos 2 líneas para nombres largos
                                                display:      "-webkit-box",
                                                WebkitLineClamp: isMobile ? 2 : 3,
                                                WebkitBoxOrient: "vertical",
                                            }}
                                        >
                                            {it.nombre_catalogo}
                                        </Typography>
                                        {renderBadge(it.nombre_tienda)}
                                    </Box>

                                    {/* Precio + botón añadir */}
                                    <Stack alignItems={isMobile ? "flex-end" : "flex-end"} spacing={0.5} sx={{ flexShrink: 0 }}>
                                        <Typography
                                            variant={isMobile ? "subtitle1" : "h5"}
                                            sx={{ fontWeight: 900, color: "text.primary", lineHeight: 1 }}
                                        >
                                            {it.precio.toFixed(2)} €
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                            /ud
                                        </Typography>

                                        {/* Botón añadir / controles cantidad */}
                                        {isSelected(it) ? (
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => decrement(it)}
                                                    sx={{ border: "1px solid", borderColor: "divider", minWidth: 32, minHeight: 32 }}
                                                >
                                                    <RemoveIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                                <Typography sx={{ fontWeight: 800, minWidth: 16, textAlign: "center", fontSize: "0.85rem" }}>
                                                    {getCantidad(it)}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => increment(it)}
                                                    sx={{ border: "1px solid", borderColor: "divider", minWidth: 32, minHeight: 32 }}
                                                >
                                                    <AddIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            </Stack>
                                        ) : (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => addItem(it)}
                                                startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                                                sx={{
                                                    borderRadius:  2,
                                                    fontWeight:    700,
                                                    textTransform: "none",
                                                    minHeight:     32,
                                                    fontSize:      "0.75rem",
                                                    px:            1.5,
                                                }}
                                            >
                                                Añadir
                                            </Button>
                                        )}
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </MotionCard>
                    ))}
                </AnimatePresence>

                {/* Estados vacíos */}
                {!loading && items.length === 0 && query && (
                    <Box sx={{ textAlign: "center", py: 6 }}>
                        <Typography variant="body1" color="text.secondary">
                            No se encontraron productos para "{query}"
                        </Typography>
                    </Box>
                )}
                {!loading && items.length === 0 && !query && (
                    <Box sx={{ textAlign: "center", py: 6, opacity: 0.4 }}>
                        <StorefrontIcon sx={{ fontSize: 56, mb: 1.5 }} />
                        <Typography variant="body1">Busca algo para empezar a comparar</Typography>
                    </Box>
                )}
            </Stack>

            <GuardarEnListaModal
                open={mostrarGuardarModal}
                onClose={() => setMostrarGuardarModal(false)}
                onConfirm={guardarSeleccion}
            />
        </Box>
    );
}